import type { LucideIcon } from 'lucide-react';
import { AlertCircle, ShieldAlert } from 'lucide-react';
import { Card } from '@command-center/ui-system';

/**
 * The panels a log viewer shows when it has no rows to show.
 *
 * Shared by the Activity and AI viewers, and separate from them, because the
 * distinction between them is the point: "we could not ask", "we asked and
 * there is nothing", and "the store answered but told us not to trust it" are
 * three different facts, and rendering any two of them the same way is how an
 * outage comes to read as a quiet week.
 */

export function LogErrorState({ title, error }: { title: string; error: unknown }) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" aria-hidden />
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        {error instanceof Error ? error.message : 'Unknown error'}
      </p>
    </Card>
  );
}

export function LogEmptyState({ icon: Icon, message }: { icon: LucideIcon; message: string }) {
  return (
    <div className="px-4 py-12 text-center text-sm text-muted-foreground">
      <div className="flex flex-col items-center gap-2">
        <Icon className="h-6 w-6 text-muted-foreground/50" aria-hidden />
        <span>{message}</span>
      </div>
    </div>
  );
}

/**
 * The store answered, and said its answer is incomplete.
 *
 * Deliberately worded as an unknown rather than a warning decoration: the whole
 * reason the API reports `degraded` instead of returning 500 is so the page
 * stays usable, and that only works if the page says what it does not know.
 */
export function LogDegradedBanner({ note }: { note?: string }) {
  return (
    <Card className="flex items-start gap-2 border-warning/40 bg-warning/5 p-3 text-[12px]">
      <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
      <span>
        {note ?? 'The log store is unavailable.'} This is not an empty timeline — treat it as
        unknown rather than as "nothing happened".
      </span>
    </Card>
  );
}

export function LogSkeleton({ className = 'h-[360px]' }: { className?: string }) {
  return <Card className={`${className} animate-pulse`} />;
}
